// frontend/src/components/channels/move-channel-dialog.tsx
// Read DESIGN.md before modifying. Title Case button labels; no em-dashes.
// Confirm modal for moving a single channel to another workspace in the same
// org. The target workspace is chosen here; the parent owns the POST + refresh.
import { useState } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface WorkspaceOption {
  publicId: string;
  name: string;
  /** Optional: when targets span both kinds, badge each so the surface is clear. */
  kind?: 'team' | 'customer';
}

interface MoveChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelName: string;
  /** Excluded from the target picker (a channel cannot move to its own workspace). */
  currentWorkspacePublicId: string;
  workspaces: WorkspaceOption[];
  moving: boolean;
  onConfirm: (targetWorkspacePublicId: string) => void;
}

export function MoveChannelDialog({
  open,
  onOpenChange,
  channelName,
  currentWorkspacePublicId,
  workspaces,
  moving,
  onConfirm,
}: MoveChannelDialogProps) {
  // The dialog is conditionally mounted per row, so a fresh mount already
  // resets this; no open-effect needed.
  const [selected, setSelected] = useState<WorkspaceOption | null>(null);

  const targets = workspaces.filter((w) => w.publicId !== currentWorkspacePublicId);
  const empty = targets.length === 0;
  // Org Channels move tags targets with a kind (workspace/customer). When tagged,
  // the picker can contain either, so widen the copy to "destination".
  const tagged = targets.some((t) => t.kind);
  const targetNounPlural = tagged ? 'destinations' : 'workspaces';

  return (
    <Dialog open={open} onOpenChange={(next) => !moving && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Channel</DialogTitle>
          <DialogDescription>
            Move {channelName} to a different{' '}
            {tagged ? 'workspace or customer' : 'workspace'} in this organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>{tagged ? 'Move To' : 'Target Workspace'}</Label>
          {/* base-ui Combobox with the default contains-matcher: the full
              workspace list is held client-side, so typing fuzzy-filters in
              memory (handles thousands of rows without a server round-trip). */}
          <Combobox.Root
            items={targets}
            value={selected}
            onValueChange={(item: WorkspaceOption | null) => setSelected(item)}
            itemToStringLabel={(item: WorkspaceOption | null) => item?.name ?? ''}
            isItemEqualToValue={(a: WorkspaceOption, b: WorkspaceOption) =>
              a?.publicId === b?.publicId
            }
            disabled={empty}
          >
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Combobox.Input
                aria-label="Target Workspace"
                placeholder={empty ? `No other ${targetNounPlural}` : `Search ${targetNounPlural}...`}
                className="flex h-9 w-full rounded-md border border-input bg-transparent pr-8 pl-8 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Combobox.Trigger
                aria-label="Open workspace list"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground outline-none hover:text-foreground"
              >
                <ChevronsUpDown className="size-4" />
              </Combobox.Trigger>
            </div>

            <Combobox.Portal>
              <Combobox.Positioner positionMethod="fixed" align="start" side="bottom" sideOffset={4} className="z-50">
                <Combobox.Popup className="w-(--anchor-width) max-h-60 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none">
                  <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                    No {targetNounPlural} match your search
                  </Combobox.Empty>
                  <Combobox.List>
                    {(item: WorkspaceOption) => (
                      <Combobox.Item
                        key={item.publicId}
                        value={item}
                        className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                      >
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        {item.kind && (
                          <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                            {item.kind === 'customer' ? 'Customer' : 'Workspace'}
                          </span>
                        )}
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={moving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!selected || moving}
            onClick={() => selected && onConfirm(selected.publicId)}
          >
            {moving && <Loader2 className="size-3.5 animate-spin" />}
            Move Channel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
